import React, { useEffect, useState, useRef } from 'react';
import { useUser } from '../../context/UserContext';
import { motion } from 'framer-motion';
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { IoEyeOutline, IoSearchSharp, IoFilterSharp } from 'react-icons/io5';
import { useSearchParams, useNavigate } from 'react-router';
import api from '../../utils/api';

function PaySlipReports() {

  const [paySlipReports, setPaySlipReports] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [requestsPerPage] = useState(8);
  const [apiLoading, setApiLoading] = useState(false);
  const { currentUser, loading } = useUser();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [selectedReport, setSelectedReport] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Filters
  const [status, setStatus] = useState("ALL");
  const [selectedManager, setSelectedManager] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [salaryPeriod, setSalaryPeriod] = useState("");
  const [managers, setManagers] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null);

  // Draft filters
  const [draftStatus, setDraftStatus] = useState("ALL");
  const [draftManager, setDraftManager] = useState("");
  const [draftLocation, setDraftLocation] = useState("");
  const [draftSalaryPeriod, setDraftSalaryPeriod] = useState("");

  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const activeFilterCount = [
    status !== "ALL" ? status : "",
    selectedManager,
    selectedLocation,
    salaryPeriod,
  ].filter(Boolean).length;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchManagers = async () => {
    try {
      const response = await api.get('/auth/managers');
      setManagers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch managers', error);
    }
  };

  useEffect(() => {
    fetchManagers();
  }, []);

  const fetchPaySlipReports = async (page = 1, size = requestsPerPage) => {
    setApiLoading(true);
    try {
      const searchTerm = searchParams.get("search") || "";

      const response = await api.get(`/api/payslips/reports`, {
        params: {
          page: page - 1,
          size,
          search: searchTerm,
          status: status === "ALL" ? null : status,
          managerId: selectedManager || null,
          location: selectedLocation || null,
          salaryPeriod: salaryPeriod || null,
        }
      });

      const data = response.data;

      const mappedData = data.content.map(req => ({
        payid: req.payid,
        employeeId: req.employeeId,
        employeeName: req.employeeName,
        salaryPeriod: req.salaryPeriod,
        daysInMonth: req.daysInMonth,
        daysPresent: req.daysPresent,
        status: req.status,
        downloadRequestedAt: req.downloadRequestedAt,
      }));

      setPaySlipReports(mappedData);
      setTotalPages(data.totalPages);
      setCurrentPage(data.currentPage + 1);
    } catch (error) {
      console.error('Failed to fetch payslip reports:', error);
    } finally {
      setApiLoading(false);
    }
  };

  useEffect(() => {
    fetchPaySlipReports(currentPage);
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    fetchPaySlipReports(1);
  }, [searchParams, status, selectedManager, selectedLocation, salaryPeriod]);

  const handleView = (report) => {
    setSelectedReport(report);
    setShowViewModal(true);
  };

  const applySearch = () => {
    navigate(`?search=${searchInput}`);
    setCurrentPage(1);
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const allIds = paySlipReports.map(r => r.payid);
    const allSelected = allIds.every(id => selectedRows.includes(id));
    setSelectedRows(allSelected ? [] : allIds);
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const params = {};
      if (selectedRows.length > 0) {
        params.ids = selectedRows.join(',');
      }
      const response = await api.get(`/api/payslips/export`, {
        params,
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "payslip_reports.xlsx";
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("Export failed", err);
    } finally {
      setExportLoading(false);
    }
  };

  // "2026-05-01T..." or LocalDateTime string → "01-05-2026"
  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "—";
    const d = new Date(dateTimeStr);
    const day   = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year  = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // "2026-05" → "May 2026"
  const formatSalaryPeriod = (period) => {
    if (!period) return "—";
    const [year, month] = period.split("-");
    const date = new Date(Number(year), Number(month) - 1, 1);
    return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "APPROVED":          return "bg-green-100 text-green-800";
      case "PENDING":           return "bg-yellow-100 text-yellow-800";
      case "REJECTED":          return "bg-red-100 text-red-800";
      case "GENERATED":         return "bg-blue-100 text-blue-800";
      case "PUBLISHED":         return "bg-purple-100 text-purple-800";
      case "DOWNLOAD_REQUESTED":return "bg-orange-100 text-orange-800";
      default:                  return "bg-slate-100 text-slate-600";
    }
  };

  if (loading || !currentUser) {
    return (
      <div className="p-4 sm:p-6 w-full mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="h-8 w-48 bg-slate-200 rounded animate-pulse"></div>
          <div className="h-10 w-32 bg-slate-200 rounded-lg animate-pulse"></div>
        </div>
        <div className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="h-[400px] flex items-center justify-center">
            <svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sm:p-6 sm:mt-auto mt-8 w-full mx-auto">

      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-1 mb-4 bg-white rounded-2xl p-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Payslip Reports</h2>
          <p className="text-sm text-slate-400">{paySlipReports.length} records shown</p>
        </div>

        <div className="flex flex-1 sm:flex-none items-center gap-3 w-full sm:w-auto">

          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search employee..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }}
              className="pl-9 pr-4 py-2 text-sm rounded-full border border-slate-200 bg-slate-50 focus:outline-none focus:border-slate-400 w-56"
            />
            <IoSearchSharp
              onClick={applySearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm cursor-pointer hover:text-slate-600"
            />
          </div>

          {/* Export Button */}
          <button
            onClick={handleExport}
            disabled={selectedRows.length === 0 || exportLoading}
            className={`px-5 py-2.5 rounded-full text-xs font-medium transition ${
              selectedRows.length === 0
                ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                : "bg-green-600 text-white hover:bg-green-700"
            }`}
          >
            {exportLoading ? "Exporting..." : `Export (${selectedRows.length})`}
          </button>

          {/* Filter Button */}
          <div className="relative" ref={filterRef}>
            <button
              onClick={() => {
                if (!filterOpen) {
                  setDraftStatus(status);
                  setDraftManager(selectedManager);
                  setDraftLocation(selectedLocation);
                  setDraftSalaryPeriod(salaryPeriod);
                }
                setFilterOpen(prev => !prev);
              }}
              className={`relative flex items-center gap-1.5 px-4 py-1.5 rounded-full border text-sm font-medium transition
                ${activeFilterCount > 0
                  ? 'bg-slate-700 text-white border-slate-700'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                }`}
            >
              <IoFilterSharp className="text-base" />
              <span>Filter</span>
              {activeFilterCount > 0 && (
                <span className="ml-1 bg-white text-slate-700 text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Filter Dropdown */}
            {filterOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 p-4 space-y-4">

                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-slate-700">Filters</span>
                  {activeFilterCount > 0 && (
                    <button
                      onClick={() => {
                        setDraftStatus("ALL");
                        setDraftManager("");
                        setDraftLocation("");
                        setDraftSalaryPeriod("");
                        setStatus("ALL");
                        setSelectedManager("");
                        setSelectedLocation("");
                        setSalaryPeriod("");
                        setCurrentPage(1);
                      }}
                      className="text-xs text-red-500 hover:text-red-600 font-medium"
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Status</label>
                  <select
                    value={draftStatus}
                    onChange={(e) => setDraftStatus(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  >
                    <option value="ALL">All Status</option>
                    <option value="GENERATED">Generated</option>
                    <option value="PUBLISHED">Published</option>
                    <option value="DOWNLOAD_REQUESTED">Download Requested</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>

                {/* Salary Period Filter */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Salary Period</label>
                  <input
                    type="month"
                    value={draftSalaryPeriod}
                    onChange={(e) => setDraftSalaryPeriod(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  />
                </div>

                {/* Manager Filter */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Manager</label>
                  <select
                    value={draftManager}
                    onChange={(e) => setDraftManager(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  >
                    <option value="">All Managers</option>
                    {managers.map((manager) => (
                      <option key={manager.id} value={manager.id}>
                        {manager.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Location Filter */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Location</label>
                  <div className="flex gap-2">
                    {['Bengaluru', 'Mangaluru'].map((loc) => (
                      <button
                        key={loc}
                        onClick={() => setDraftLocation(prev => prev === loc ? '' : loc)}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition
                          ${draftLocation === loc
                            ? 'bg-slate-700 text-white border-slate-700'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                          }`}
                      >
                        {loc}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Apply Button */}
                <button
                  onClick={() => {
                    setStatus(draftStatus);
                    setSelectedManager(draftManager);
                    setSelectedLocation(draftLocation);
                    setSalaryPeriod(draftSalaryPeriod);
                    setCurrentPage(1);
                    setFilterOpen(false);
                  }}
                  className="w-full bg-slate-700 text-white py-2 rounded-lg text-sm font-medium hover:bg-slate-600 transition"
                >
                  Apply Filters
                </button>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Table */}
      <motion.div
        className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-100">
              <tr>
                <th className="px-3 py-4 text-left">
                  <input
                    type="checkbox"
                    checked={paySlipReports.length > 0 && paySlipReports.every(r => selectedRows.includes(r.payid))}
                    onChange={handleSelectAll}
                    className="w-4 h-4"
                  />
                </th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Sl. No</th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Employee ID</th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Employee Name</th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Salary Period</th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Days Present</th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Download Requested</th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Status</th>
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-center">View</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {apiLoading ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                      </svg>
                    </div>
                  </td>
                </tr>
              ) : paySlipReports.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-slate-500">No payslip records found.</td>
                </tr>
              ) : (
                paySlipReports.map((report, index) => (
                  <tr key={report.payid} className="hover:bg-slate-50/50 transition-colors duration-150">
                    <td className="px-3 py-2 align-middle">
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(report.payid)}
                        onChange={() => handleSelectRow(report.payid)}
                        className="w-4 h-4"
                      />
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-600">{(currentPage - 1) * requestsPerPage + index + 1}</td>
                    <td className="px-3 py-2 text-sm"><div className="font-medium text-slate-700">{report.employeeId}</div></td>
                    <td className="px-3 py-2 text-sm"><div className="font-medium text-slate-700">{report.employeeName}</div></td>
                    <td className="px-3 py-2 text-sm text-slate-600">{formatSalaryPeriod(report.salaryPeriod)}</td>
                    <td className="px-3 py-2 text-sm text-slate-600">
                      {report.daysPresent ?? "—"} / {report.daysInMonth ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-600">
                      {formatDateTime(report.downloadRequestedAt)}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusStyle(report.status)}`}>
                        {report.status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-center">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleView(report)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="View Details"
                        >
                          <IoEyeOutline className="text-xl" />
                        </motion.button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {paySlipReports.length > 0 && (
            <div className="px-6 py-4 border-t border-slate-100">
              <Stack spacing={2} className="flex justify-end">
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  variant="outlined"
                  shape="full"
                  size="small"
                  showFirstButton
                  showLastButton
                  sx={{
                    "& .MuiPaginationItem-root": { color: "#475569", borderColor: "#e2e8f0" },
                    "& .Mui-selected": { backgroundColor: "#475569 !important", color: "#fff" },
                  }}
                />
              </Stack>
            </div>
          )}
        </div>
      </motion.div>

      {/* View Modal */}
      {showViewModal && selectedReport && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center"
          onClick={(e) => { if (e.target === e.currentTarget) setShowViewModal(false); }}
        >
          <motion.div
            className="relative bg-white rounded-3xl shadow-2xl w-full max-w-xl mx-4 p-6 sm:p-8"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <button
              onClick={() => setShowViewModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-red-500 transition"
              aria-label="Close modal"
            >✕</button>

            <div className="mb-6 text-center">
              <h2 className="text-2xl font-semibold text-slate-800">Payslip Details</h2>
              <p className="text-sm text-slate-500">Full payslip summary for selected record</p>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-slate-500">Employee Name</p>
                  <p className="font-medium text-slate-900">{selectedReport.employeeName}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Employee ID</p>
                  <p className="font-medium text-slate-900">{selectedReport.employeeId}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-slate-500">Salary Period</p>
                  <p className="font-medium text-blue-600">{formatSalaryPeriod(selectedReport.salaryPeriod)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusStyle(selectedReport.status)}`}>
                    {selectedReport.status}
                  </span>
                </div>
              </div>

              <div>
                <p className="text-sm text-slate-500">Days Present</p>
                <p className="font-medium text-slate-900">
                  {selectedReport.daysPresent ?? "—"} / {selectedReport.daysInMonth ?? "—"} days
                </p>
              </div>

              <div>
                <p className="text-sm text-slate-500">Download Requested At</p>
                <p className="font-medium text-slate-900">{formatDateTime(selectedReport.downloadRequestedAt)}</p>
              </div>
            </div>

            <div className="mt-8 flex justify-center">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-6 py-2 rounded-full bg-slate-800 text-white hover:bg-slate-700 transition"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}

    </div>
  );
}

export default PaySlipReports;